/**
 * PropertyForm — shared property-listing form body.
 *
 * Extracted from the broker create page (Task 1.3) so the create/edit pages
 * become thin wrappers that own only auth/subscription/FAL/Nafath gating and
 * the submit orchestration (image upload + POST). This component owns the form
 * UI and all form-local state/handlers, and hands the collected form + image
 * files back to the wrapper via `onSubmit`.
 *
 * It deliberately contains NO network calls, auth, subscription, or FAL/Nafath
 * gating — those stay in the page.
 */

import { useMemo, useState, useEffect, useCallback, useRef, useId } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../base/Button';
import DomainIcon from '../base/icons/domain/DomainIcon';
import OptionTile, { OptionTileGroup } from '../base/OptionTile';
import AddressAutocomplete, { type AddressComponents } from '../base/AddressAutocomplete';
import {
  RESIDENTIAL_TYPES, COMMERCIAL_TYPES, AMENITIES_LIST, AMENITY_GROUPS, UTILITIES_LIST, PROPERTY_CONDITIONS, HOUSE_STYLES,
  RESIDENTIAL_TYPE_OPTIONS, ORIENTATION_OPTIONS, DEED_STATUS_OPTIONS, CONSTRUCTION_STATUS_OPTIONS,
  OWNER_TYPE_OPTIONS, AD_CHANNEL_OPTIONS, UTILITY_MUTEX_GROUPS,
  CONSTRUCTION_STAGE_OPTIONS, getListingStatusOptions,
} from '../../config/propertyFormOptions';
import type { OptionItem } from '../../config/propertyFormOptions';
import type { HouseStyle } from '../../config/propertyFormOptions';
import { getAllowedAmenities, getVisibleFieldGroups } from '../../config/propertyTypeConfig';
import {
  type FormState,
  type PropertyCategory,
  type FurnishingStatus,
  type PropertyCondition,
  INITIAL_FORM,
  numberFromInput,
  requiresAmenities,
  validateForm,
  pruneAmenitiesForType,
  toggleInList,
} from '../../utils/propertyFormState';
import { formatAmount } from '../../utils/numberFormat';

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

const MAX_IMAGES = 15;

// Phase 4 property intake v2 — gates the additive "Additional details" section
// (category / facade / flats+shops / offerings / display status). Flag OFF =>
// the listing form is byte-identical to before. The fields themselves still
// round-trip through the payload + edit prefill (see propertyFormState.ts).
const INTAKE_V2_ENABLED = import.meta.env.VITE_PROPERTY_INTAKE_V2_ENABLED === 'true';

// Backend Property.offeringTypes enum (multi-select). listingType stays primary.
const OFFERING_OPTIONS: { value: string; label: string; labelAr: string; icon: string }[] = [
  { value: 'SALE', label: 'For sale', labelAr: 'للبيع', icon: 'ri-price-tag-3-line' },
  { value: 'RENT', label: 'For rent', labelAr: 'للإيجار', icon: 'ri-key-2-line' },
  { value: 'SHORT_RENT', label: 'Short-term rent', labelAr: 'إيجار قصير', icon: 'ri-calendar-event-line' },
  { value: 'SEEKING_INVESTMENT', label: 'Seeking investment', labelAr: 'يبحث عن استثمار', icon: 'ri-line-chart-line' },
  { value: 'SEEKING_DEVELOPER', label: 'Seeking developer', labelAr: 'يبحث عن مطوّر', icon: 'ri-building-4-line' },
];
const FACADE_OPTIONS: { value: string; label: string; labelAr: string }[] = [
  { value: 'NORTH', label: 'North', labelAr: 'شمالية' },
  { value: 'SOUTH', label: 'South', labelAr: 'جنوبية' },
  { value: 'EAST', label: 'East', labelAr: 'شرقية' },
  { value: 'WEST', label: 'West', labelAr: 'غربية' },
  { value: 'CORNER', label: 'Corner', labelAr: 'زاوية' },
];

// Build the set of preview indices that should be pre-tagged as floor plans,
// by matching the given floor-plan URLs against the displayed preview list.
const floorPlanIndicesFromUrls = (previews: string[], floorPlanUrls?: string[]): Set<number> => {
  if (!floorPlanUrls || floorPlanUrls.length === 0) return new Set();
  const wanted = new Set(floorPlanUrls);
  const out = new Set<number>();
  previews.forEach((url, i) => {
    if (wanted.has(url)) out.add(i);
  });
  return out;
};

// ---------------------------------------------------------------------------
// Component contract
// ---------------------------------------------------------------------------

export type PropertyFormVariant = 'broker' | 'individual' | 'edit';

export interface PropertyFormSubmit {
  form: FormState;
  imageFiles: File[];
  coverImageIndex: number;
  floorPlanIndices: Set<number>;
  // Full ordered list of currently-displayed image previews. For the edit
  // variant this mixes existing stored URLs (kept images, http/https) with
  // new-file previews (data:/blob:). `coverImageIndex` and `floorPlanIndices`
  // index into THIS list. The create wrapper can ignore it and keep using
  // `imageFiles` — the field is additive.
  imagePreviews: string[];
}

export interface PropertyFormProps {
  variant: PropertyFormVariant;
  initialForm?: FormState;            // edit prefill (from propertyToFormState)
  initialImageUrls?: string[];        // edit: existing gallery (URLs already uploaded)
  // edit: existing image URLs that were previously tagged as floor plans. Used
  // to pre-tag those slots so they display as floor plans and stay editable
  // (tag/untag). Matched against initialImageUrls by value.
  initialFloorPlanUrls?: string[];
  showListUnder: boolean;             // broker + company-linked only
  companyName?: string | null;
  personalOptionLocked?: boolean;     // inherited-sub brokers
  submitting: boolean;
  serverErrors?: Record<string, string>;
  submitLabel: string;
  onSubmit: (data: PropertyFormSubmit) => void | Promise<void>;
  // --- Optional extras needed to fully preserve the create page's behavior. ---
  // The page derives a "form disabled" flag from its gates (listing limit,
  // FAL/Nafath). When disabled, both buttons are disabled and the submit
  // button shows a "limit reached" label, exactly as before.
  formDisabled?: boolean;
  disabledLabel?: string;
  // Live upload progress text shown inside the submit button while submitting.
  uploadProgress?: string | null;
  // Optional reset handler — when provided the "Reset Form" button is shown.
  // The create page passes this so the existing reset button stays identical.
  onReset?: () => void;
  resetLabel?: string;
  // Incremented by the wrapper after a successful submit to clear the form
  // (the create page reset the form on success). Any change to this value
  // re-initializes the form-local state.
  resetSignal?: number;
}

export default function PropertyForm(props: PropertyFormProps) {
  const {
    // Drives small copy differences: the contact section reads as the OWNER's
    // (private) details for the individual variant vs. the agent's for brokers.
    variant,
    initialForm,
    initialImageUrls,
    initialFloorPlanUrls,
    showListUnder,
    companyName,
    personalOptionLocked = false,
    submitting,
    serverErrors,
    submitLabel,
    onSubmit,
    formDisabled = false,
    disabledLabel,
    uploadProgress = null,
    onReset,
    resetLabel,
    resetSignal,
  } = props;

  const { t, i18n } = useTranslation(['propertyCreate', 'search', 'common']);
  const dir = i18n.dir();
  const isRTL = dir === 'rtl';
  const lang: 'en' | 'ar' = isRTL ? 'ar' : 'en';

  // Ids that name the tile groups after their visible headings (and tie hints
  // and the amenities error to them) for screen readers.
  const tileIds = useId();
  const typeHeadingId = `${tileIds}-type`;
  const amenitiesErrorId = `${tileIds}-amenities-error`;
  const utilitiesHeadingId = `${tileIds}-utilities`;
  const utilitiesHintId = `${tileIds}-utilities-hint`;

  // -------------------------------------------------------------------------
  // Form-local state
  // -------------------------------------------------------------------------
  // Prune stale amenities on edit prefill so a value saved before per-type
  // gating (e.g. a building that still had 'balcony') doesn't persist. Pruning
  // only ever removes now-disallowed amenities — it never touches user edits.
  const [form, setForm] = useState<FormState>(
    initialForm ? pruneAmenitiesForType(initialForm) : { ...INITIAL_FORM },
  );
  const [imageFiles, setImageFiles] = useState<File[]>([]);  // Actual files for upload
  const [imagePreviews, setImagePreviews] = useState<string[]>(initialImageUrls ?? []);  // Preview URLs (existing URLs have no File)
  const [coverImageIndex, setCoverImageIndex] = useState(0);  // Index of cover image
  // floorPlanIndices: set of indices (into imagePreviews) tagged as floor plans.
  // Seeded for edit from previously-saved floor-plan URLs.
  const [floorPlanIndices, setFloorPlanIndices] = useState<Set<number>>(
    () => floorPlanIndicesFromUrls(initialImageUrls ?? [], initialFloorPlanUrls),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  // REGA documents/licence section: expanded for broker/company (they must fill
  // the licence numbers), collapsed for individual platform-managed listings.
  const [showRegaSection, setShowRegaSection] = useState(variant !== 'individual');

  // Re-seed when the wrapper supplies new initial data (edit prefill arriving
  // after an async load). The create variant never changes these props.
  useEffect(() => {
    if (initialForm) setForm(pruneAmenitiesForType(initialForm));
  }, [initialForm]);

  // When the wrapper supplies a new existing-gallery list (async edit prefill,
  // or the persisted order after a successful save), re-seed the preview list
  // and the floor-plan tags together. Any new files are now reflected in the
  // incoming URLs, so the local file buffer is cleared.
  useEffect(() => {
    if (!initialImageUrls) return;
    setImagePreviews(initialImageUrls);
    setImageFiles([]);
    setCoverImageIndex(0);
    setFloorPlanIndices(floorPlanIndicesFromUrls(initialImageUrls, initialFloorPlanUrls));
    // initialFloorPlanUrls intentionally read fresh; keyed on the gallery list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialImageUrls]);

  // Reset form-local state when the wrapper bumps resetSignal (e.g. after a
  // successful submit). Skipped on the initial mount so a 0/undefined signal
  // doesn't wipe edit prefill.
  const firstResetRun = useRef(true);
  useEffect(() => {
    if (firstResetRun.current) {
      firstResetRun.current = false;
      return;
    }
    setForm(initialForm ? pruneAmenitiesForType(initialForm) : { ...INITIAL_FORM });
    setImageFiles([]);
    setImagePreviews(initialImageUrls ?? []);
    setCoverImageIndex(0);
    setFloorPlanIndices(floorPlanIndicesFromUrls(initialImageUrls ?? [], initialFloorPlanUrls));
    setErrors({});
    // initialForm/initialImageUrls intentionally read fresh; we only re-run on
    // resetSignal changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignal]);

  // Merge server-side errors (from the wrapper) into the displayed errors so a
  // failed POST surfaces under the form just like local validation does.
  const displayedErrors = useMemo(
    () => ({ ...errors, ...(serverErrors ?? {}) }),
    [errors, serverErrors],
  );

  // Get current property types based on category
  const propertyTypes = form.category === 'residential' ? RESIDENTIAL_TYPES : COMMERCIAL_TYPES;

  // Price per sqm calculation. form.sqft holds m² (Saudi convention) despite the field name.
  const pricePerSqm = useMemo(() => {
    const price = numberFromInput(form.price);
    const sqm = numberFromInput(form.sqft);
    if (!Number.isFinite(price) || !Number.isFinite(sqm) || sqm <= 0) {
      return null;
    }
    return Math.round(price / sqm);
  }, [form.price, form.sqft]);

  // -------------------------------------------------------------------------
  // Form-local handlers
  // -------------------------------------------------------------------------
  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (category: PropertyCategory) => {
    const newTypes = category === 'residential' ? RESIDENTIAL_TYPES : COMMERCIAL_TYPES;
    setForm((prev) => ({
      ...prev,
      category,
      propertyType: newTypes[0].id,
    }));
  };

  const handlePropertyTypeChange = (type: string) => {
    setForm((prev) => pruneAmenitiesForType({ ...prev, propertyType: type }));
  };

  const handleAmenityToggle = (amenityId: string) => {
    setForm((prev) => {
      const currentAmenities = prev.amenities;
      if (currentAmenities.includes(amenityId)) {
        return { ...prev, amenities: currentAmenities.filter((a) => a !== amenityId) };
      }
      return { ...prev, amenities: [...currentAmenities, amenityId] };
    });
  };

  const handleAddressSelect = useCallback((address: AddressComponents) => {
    console.log('Address selected, updating form:', address);
    setForm((prev) => ({
      ...prev,
      // Use formattedAddress as street address if streetAddress is empty
      streetAddress: address.streetAddress || address.formattedAddress.split(',')[0] || '',
      city: address.city || prev.city,
      region: address.region || prev.region,
      district: address.district || prev.district,
      postalCode: address.postalCode || prev.postalCode,
      latitude: address.latitude ? address.latitude.toString() : prev.latitude,
      longitude: address.longitude ? address.longitude.toString() : prev.longitude,
    }));
  }, []);

  const handleImageFiles = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    try {
      const availableSlots = MAX_IMAGES - imageFiles.length;
      const newFiles = Array.from(files).slice(0, availableSlots);
      const newPreviews = await Promise.all(newFiles.map(readFileAsDataUrl));

      setImageFiles(prev => [...prev, ...newFiles]);
      setImagePreviews(prev => [...prev, ...newPreviews]);
    } catch (error) {
      console.warn('Image preview failed', error);
      setErrors((prev) => ({ ...prev, images: t('propertyCreate.validation.imageUploadFailed') }));
    }
  };

  const handleRemoveImage = (index: number) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    // Adjust cover index if needed
    setCoverImageIndex(prev => {
      if (index < prev) return prev - 1;
      if (index === prev) return 0;
      return prev;
    });
    // Rebuild floor plan indices: remove the deleted index, shift down higher indices
    setFloorPlanIndices(prev => {
      const next = new Set<number>();
      for (const i of prev) {
        if (i < index) next.add(i);
        else if (i > index) next.add(i - 1);
        // i === index → removed, not added
      }
      return next;
    });
  };

  const handleSetCoverImage = (index: number) => {
    if (index >= 0 && index < imagePreviews.length) {
      setCoverImageIndex(index);
    }
  };

  const handleToggleFloorPlan = (index: number) => {
    setFloorPlanIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // Resets the form-local state to its initial values (form fields, images,
  // cover/floor-plan tags, errors), then lets the wrapper clear any page-level
  // state (upload progress, banners) via the optional onReset callback. This
  // mirrors the create page's original resetForm exactly.
  const handleResetClick = () => {
    setForm(initialForm ? pruneAmenitiesForType(initialForm) : { ...INITIAL_FORM });
    setImageFiles([]);
    setImagePreviews(initialImageUrls ?? []);
    setCoverImageIndex(0);
    setFloorPlanIndices(floorPlanIndicesFromUrls(initialImageUrls ?? [], initialFloorPlanUrls));
    setErrors({});
    onReset?.();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrors({});

    const validation = validateForm(form, { lang, variant });
    if (Object.keys(validation).length) {
      setErrors(validation);
      const firstErrorField = Object.keys(validation)[0];
      const errorElement = document.getElementById(firstErrorField);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
      return;
    }

    await onSubmit({ form, imageFiles, coverImageIndex, floorPlanIndices, imagePreviews });
  };

  // -------------------------------------------------------------------------
  // Derived view flags (property-type dependent field visibility)
  // -------------------------------------------------------------------------
  const fieldGroups = getVisibleFieldGroups(form.propertyType);
  const allowedAmenities = getAllowedAmenities(form.propertyType, form.listingType);

  const showBedrooms = fieldGroups.bedrooms;
  const showBathrooms = fieldGroups.bathrooms;
  const showLivingRooms = fieldGroups.livingRooms;
  const showKitchens = fieldGroups.kitchens;
  const showAttachment = fieldGroups.attachment;
  const showFurnishingField = fieldGroups.furnishing;
  const showFloorField = fieldGroups.floorInfo;
  const showPricingFields = fieldGroups.price;
  const showAmenitiesSection = fieldGroups.amenities;
  const showParkingField = fieldGroups.parking;
  const showTotalUnitsField = fieldGroups.totalUnits;
  const showAmenitiesRequired = requiresAmenities(form.propertyType);

  // Contact section copy. For an individual FAL-managed listing these fields are
  // the OWNER's private contact (kept off the public listing — the platform
  // agent handles inquiries), so the heading + help text differ from the
  // broker/edit variants where they're the public agent contact.
  const isIndividual = variant === 'individual';
  // Broker/company listings carry their own REGA advertising licence, so the
  // licence-number fields are required and the documents section opens expanded.
  const isBrokerLike = variant !== 'individual';
  const contactHeading = isIndividual
    ? (isRTL ? 'معلومات المالك' : 'Owner Information')
    : (isRTL ? 'معلومات التواصل' : 'Contact Information');
  const contactHelp = isIndividual
    ? (isRTL
        ? 'تبقى بياناتك خاصة ولا تظهر في الإعلان. تتولى المنصة التعامل مع استفسارات المهتمين نيابةً عنك.'
        : 'Your details stay private and are not shown on the listing. The platform handles buyer inquiries on your behalf.')
    : null;

  return (
    <form className="space-y-8 rounded-xl bg-white p-6 shadow-lg" onSubmit={handleSubmit} noValidate>
      {/* Property Category */}
      <section>
        <h2 className="text-xl font-semibold text-[var(--pv-color-primary)] mb-4">
          <i className="ri-home-line me-2 text-[#004D40]"></i>
          {isRTL ? 'فئة العقار' : 'Property Category'}
        </h2>
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => handleCategoryChange('residential')}
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-xl border-2 transition-all ${
              form.category === 'residential'
                ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] text-[#003930]'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <i className="ri-home-5-line text-2xl"></i>
            <span className="font-medium">{isRTL ? 'سكني' : 'Residential'}</span>
          </button>
          <button
            type="button"
            onClick={() => handleCategoryChange('commercial')}
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-xl border-2 transition-all ${
              form.category === 'commercial'
                ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] text-[#003930]'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <i className="ri-building-line text-2xl"></i>
            <span className="font-medium">{isRTL ? 'تجاري' : 'Commercial'}</span>
          </button>
        </div>
      </section>

      {/* Property Type */}
      <section>
        <h2 id={typeHeadingId} className="text-xl font-semibold text-[var(--pv-color-primary)] mb-4">
          <i className="ri-building-4-line me-2 text-[#004D40]"></i>
          {isRTL ? 'نوع العقار' : 'Property Type'}
        </h2>
        {/* One radio group: Tab lands on the chosen type, arrows move and choose
            (mirrored in RTL). Row tiles keep label, icon and icon tile in the rem
            proportion (THEME.md); `break-words` stops a long English word from
            spilling out of a narrow phone column. */}
        <OptionTileGroup
          role="radiogroup"
          aria-labelledby={typeHeadingId}
          className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4"
        >
          {propertyTypes.map((type) => (
            <OptionTile
              key={type.id}
              role="radio"
              layout="row"
              size="md"
              selected={form.propertyType === type.id}
              onSelect={() => handlePropertyTypeChange(type.id)}
              icon={<DomainIcon id={type.id} />}
              label={isRTL ? type.labelAr : type.label}
              className="break-words"
            />
          ))}
        </OptionTileGroup>
      </section>

      {/* Listing Type */}
      <section className="grid gap-6 md:grid-cols-2">
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            <i className="ri-price-tag-3-line me-1 text-[#004D40]"></i>
            {isRTL ? 'نوع الإعلان' : 'Listing Type'}
          </label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setForm((prev) => pruneAmenitiesForType({ ...prev, listingType: 'sale' }))}
              aria-pressed={form.listingType === 'sale'}
              className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                form.listingType === 'sale'
                  ? 'border-[var(--pv-color-primary)] bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)]'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <i className="ri-shopping-bag-line me-2"></i>
              {isRTL ? 'للبيع' : 'For Sale'}
            </button>
            <button
              type="button"
              onClick={() => setForm((prev) => ({ ...prev, listingType: 'rent' }))}
              aria-pressed={form.listingType === 'rent'}
              className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                form.listingType === 'rent'
                  ? 'border-[var(--pv-color-primary)] bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)]'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <i className="ri-key-line me-2"></i>
              {isRTL ? 'للإيجار' : 'For Rent'}
            </button>
          </div>
        </div>

        {/* "List under" picker — only shown for brokers who are currently
            a company team member. Independent brokers don't see this
            section; their listings are always personal. */}
        {showListUnder && (
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              <i className="ri-shield-check-line me-1 text-[#004D40]"></i>
              {isRTL ? 'إدراج تحت' : 'List under'}
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, listUnder: 'company' }))}
                aria-pressed={form.listUnder === 'company'}
                className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all text-start ${
                  form.listUnder === 'company'
                    ? 'border-[var(--pv-color-primary)] bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)]'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <i className="ri-building-2-line text-lg" />
                  <span className="truncate">
                    {companyName}
                  </span>
                </div>
                <div className="text-xs font-normal text-gray-500 mt-1">
                  {isRTL
                    ? 'تحت رخصة الشركة. تبقى مع الشركة إذا غادرت.'
                    : "Under the company's FAL. Stays with the company if you leave."}
                </div>
              </button>
              <button
                type="button"
                disabled={personalOptionLocked}
                onClick={() => {
                  if (personalOptionLocked) return;
                  setForm((prev) => ({ ...prev, listUnder: 'me' }));
                }}
                aria-pressed={form.listUnder === 'me'}
                title={personalOptionLocked
                  ? (isRTL
                      ? 'يتطلب نشر إعلان شخصي اشتراكاً خاصاً بك. اشتراك الشركة الموروث لا يغطي الإعلانات الشخصية.'
                      : 'Personal listings require your own subscription. The inherited company subscription does not cover personal listings.')
                  : undefined}
                className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all text-start ${
                  personalOptionLocked
                    ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                    : form.listUnder === 'me'
                      ? 'border-[var(--pv-color-primary)] bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)]'
                      : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <i className={`text-lg ${personalOptionLocked ? 'ri-lock-2-line' : 'ri-user-line'}`} />
                  <span>{isRTL ? 'باسمي' : 'Me'}</span>
                </div>
                <div className={`text-xs font-normal mt-1 ${personalOptionLocked ? 'text-gray-400' : 'text-gray-500'}`}>
                  {personalOptionLocked
                    ? (isRTL
                        ? 'يتطلب اشتراكاً شخصياً'
                        : 'Requires your own subscription')
                    : (isRTL
                        ? 'إعلان شخصي. يبقى معك إذا غادرت الشركة.'
                        : 'Personal listing. Stays with you if you leave the company.')}
                </div>
              </button>
            </div>
          </div>
        )}

        {form.listingType === 'rent' && (
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              {isRTL ? 'فترة الإيجار' : 'Rent Period'}
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, pricePeriod: 'monthly' }))}
                className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                  form.pricePeriod === 'monthly'
                    ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] text-[#003930]'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {isRTL ? 'شهري' : 'Monthly'}
              </button>
              <button
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, pricePeriod: 'quarterly' }))}
                className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                  form.pricePeriod === 'quarterly'
                    ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] text-[#003930]'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {isRTL ? 'كل 3 أشهر' : 'Quarterly'}
              </button>
              <button
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, pricePeriod: 'yearly' }))}
                className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                  form.pricePeriod === 'yearly'
                    ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] text-[#003930]'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {isRTL ? 'سنوي' : 'Yearly'}
              </button>
            </div>
          </div>
        )}

        {/* Listing status — owner-editable on edit. Reflects the DB and drives
            the public badge (Available / Under Negotiation / Rented / Sold).
            Options are filtered by sale vs. rent. */}
        {variant === 'edit' && (
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              <i className="ri-flag-line me-1 text-[#004D40]"></i>
              {isRTL ? 'حالة الإعلان' : 'Listing Status'}
            </label>
            <div className="flex flex-wrap gap-2">
              {getListingStatusOptions(form.listingType).map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, status: opt.id as FormState['status'] }))}
                  className={`py-2.5 px-4 rounded-lg border-2 text-sm font-medium transition-all ${
                    form.status === opt.id
                      ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] text-[#003930]'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <i className={`${opt.icon} me-1.5`}></i>
                  {isRTL ? opt.labelAr : opt.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Basic Info */}
      <section className="grid gap-6 md:grid-cols-2">
        <div className="md:col-span-2">
          <h2 className="text-xl font-semibold text-[var(--pv-color-primary)] mb-4">
            <i className="ri-file-text-line me-2 text-[#004D40]"></i>
            {isRTL ? 'المعلومات الأساسية' : 'Basic Information'}
          </h2>
        </div>
        <div className="md:col-span-2">
          <label className="text-sm font-medium text-gray-700" htmlFor="title">
            {isRTL ? 'عنوان العقار' : 'Property Title'} *
          </label>
          <input
            id="title"
            name="title"
            value={form.title}
            onChange={handleChange}
            className={`mt-1 w-full rounded-lg border px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB] ${
              displayedErrors.title ? 'border-red-400' : 'border-gray-300'
            }`}
            placeholder={isRTL ? 'مثال: فيلا فاخرة في جدة' : 'e.g., Luxury Villa in Jeddah'}
          />
          {displayedErrors.title && <p className="mt-1 text-sm text-red-600">{displayedErrors.title}</p>}
        </div>

        {showPricingFields && (
          <div>
            <label className="text-sm font-medium text-gray-700" htmlFor="price">
              {isRTL ? 'السعر (ر.س)' : 'Price (SAR)'} *
            </label>
            <input
              id="price"
              name="price"
              value={form.price}
              onChange={handleChange}
              className={`mt-1 w-full rounded-lg border px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB] ${
                displayedErrors.price ? 'border-red-400' : 'border-gray-300'
              }`}
              placeholder="500,000"
            />
            {displayedErrors.price && <p className="mt-1 text-sm text-red-600">{displayedErrors.price}</p>}
            {pricePerSqm && (
              <p className="mt-1 text-xs text-gray-500">
                ≈ {formatAmount(pricePerSqm)} {isRTL ? 'ر.س/م²' : 'SAR/m²'}
              </p>
            )}
          </div>
        )}

        <div>
          <label className="text-sm font-medium text-gray-700" htmlFor="sqft">
            {isRTL ? 'المساحة (متر مربع)' : 'Area (m²)'} *
          </label>
          <input
            id="sqft"
            name="sqft"
            value={form.sqft}
            onChange={handleChange}
            className={`mt-1 w-full rounded-lg border px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB] ${
              displayedErrors.sqft ? 'border-red-400' : 'border-gray-300'
            }`}
            placeholder="2,500"
          />
          {displayedErrors.sqft && <p className="mt-1 text-sm text-red-600">{displayedErrors.sqft}</p>}
        </div>

        {showBedrooms && (
          <div>
            <label className="text-sm font-medium text-gray-700" htmlFor="bedrooms">
              <i className="ri-hotel-bed-line me-1 text-gray-500"></i>
              {isRTL ? 'غرف النوم' : 'Bedrooms'} *
            </label>
            <select
              id="bedrooms"
              name="bedrooms"
              value={form.bedrooms}
              onChange={handleChange}
              className={`mt-1 w-full rounded-lg border px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB] ${
                displayedErrors.bedrooms ? 'border-red-400' : 'border-gray-300'
              }`}
            >
              <option value="">{isRTL ? 'اختر' : 'Select'}</option>
              {Array.from({ length: 11 }, (_, i) => i).map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            {displayedErrors.bedrooms && <p className="mt-1 text-sm text-red-600">{displayedErrors.bedrooms}</p>}
          </div>
        )}
        {showBathrooms && (
          <div>
            <label className="text-sm font-medium text-gray-700" htmlFor="bathrooms">
              <i className="ri-drop-line me-1 text-gray-500"></i>
              {isRTL ? 'دورات المياه' : 'Bathrooms'} *
            </label>
            <select
              id="bathrooms"
              name="bathrooms"
              value={form.bathrooms}
              onChange={handleChange}
              className={`mt-1 w-full rounded-lg border px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB] ${
                displayedErrors.bathrooms ? 'border-red-400' : 'border-gray-300'
              }`}
            >
              <option value="">{isRTL ? 'اختر' : 'Select'}</option>
              {Array.from({ length: 11 }, (_, i) => i).map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            {displayedErrors.bathrooms && <p className="mt-1 text-sm text-red-600">{displayedErrors.bathrooms}</p>}
          </div>
        )}
        {showLivingRooms && (
          <div>
            <label className="text-sm font-medium text-gray-700" htmlFor="livingRooms">
              <i className="ri-sofa-line me-1 text-gray-500"></i>
              {isRTL ? 'غرف المعيشة' : 'Living Rooms'}
            </label>
            <input
              id="livingRooms"
              type="number"
              name="livingRooms"
              min="0"
              value={form.livingRooms}
              onChange={handleChange}
              className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
            />
          </div>
        )}
        {showKitchens && (
          <div>
            <label className="text-sm font-medium text-gray-700" htmlFor="kitchens">
              <i className="ri-restaurant-line me-1 text-gray-500"></i>
              {isRTL ? 'المطابخ' : 'Kitchens'}
            </label>
            <input
              id="kitchens"
              type="number"
              name="kitchens"
              min="0"
              value={form.kitchens}
              onChange={handleChange}
              className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
            />
          </div>
        )}

        {showFloorField && (
          <>
            <div>
              <label className="text-sm font-medium text-gray-700" htmlFor="floor">
                <i className="ri-stack-line me-1 text-gray-500"></i>
                {isRTL ? 'رقم الطابق' : 'Floor Number'}
              </label>
              <input
                id="floor"
                type="number"
                name="floor"
                min="0"
                value={form.floor}
                onChange={handleChange}
                className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700" htmlFor="totalFloors">
                <i className="ri-building-line me-1 text-gray-500"></i>
                {isRTL ? 'إجمالي الطوابق' : 'Total Floors'}
              </label>
              <input
                id="totalFloors"
                type="number"
                name="totalFloors"
                min="1"
                value={form.totalFloors}
                onChange={handleChange}
                className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
              />
            </div>
          </>
        )}

        <div>
          <label className="text-sm font-medium text-gray-700" htmlFor="yearBuilt">
            <i className="ri-calendar-line me-1 text-gray-500"></i>
            {isRTL ? 'سنة البناء' : 'Year Built'}
          </label>
          <input
            id="yearBuilt"
            type="number"
            name="yearBuilt"
            min="1900"
            max={new Date().getFullYear()}
            value={form.yearBuilt}
            onChange={handleChange}
            className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
          />
        </div>

        {showParkingField && (
          <div>
            <label className="text-sm font-medium text-gray-700" htmlFor="parkingSpaces">
              <i className="ri-parking-box-line me-1 text-gray-500"></i>
              {isRTL ? 'مواقف السيارات' : 'Parking Spaces'}
            </label>
            <input
              id="parkingSpaces"
              type="number"
              name="parkingSpaces"
              min="0"
              value={form.parkingSpaces}
              onChange={handleChange}
              className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
            />
          </div>
        )}

        {showTotalUnitsField && (
          <div>
            <label className="text-sm font-medium text-gray-700" htmlFor="totalUnits">
              <i className="ri-building-4-line me-1 text-gray-500"></i>
              {isRTL ? 'عدد الوحدات' : 'Total Units'}
            </label>
            <input
              id="totalUnits"
              type="number"
              name="totalUnits"
              min="1"
              value={form.totalUnits}
              onChange={handleChange}
              className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
              placeholder={isRTL ? 'مثال: 20' : 'e.g., 20'}
            />
          </div>
        )}

        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            <i className="ri-home-heart-line me-1 text-gray-500"></i>
            {isRTL ? 'حالة العقار' : 'Property Condition'}
          </label>
          <div className="flex flex-wrap gap-2">
            {PROPERTY_CONDITIONS.map((condition) => (
              <button
                key={condition.id}
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, propertyCondition: condition.id as PropertyCondition }))}
                className={`py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
                  form.propertyCondition === condition.id
                    ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] text-[#003930]'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {isRTL ? condition.labelAr : condition.label}
              </button>
            ))}
          </div>
        </div>

        {/* House attachment style (Rightmove-style) — residential houses only.
            Toggle off when re-selecting the active chip. */}
        {showAttachment && (
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              <i className="ri-community-line me-1 text-gray-500"></i>
              {isRTL ? 'نوع الاتصال' : 'Property Style'}
            </label>
            <div className="flex flex-wrap gap-2">
              {HOUSE_STYLES.map((style) => (
                <button
                  key={style.id}
                  type="button"
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      houseStyle: (prev.houseStyle === style.id ? '' : style.id) as HouseStyle,
                    }))
                  }
                  className={`py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
                    form.houseStyle === style.id
                      ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] text-[#003930]'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <i className={`${style.icon} me-1`}></i>
                  {isRTL ? style.labelAr : style.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {showFurnishingField && (
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              <i className="ri-sofa-line me-1 text-gray-500"></i>
              {isRTL ? 'حالة التأثيث' : 'Furnishing Status'}
            </label>
            <div className="flex gap-2">
              {[
                { id: 'unfurnished', label: isRTL ? 'غير مؤثث' : 'Unfurnished' },
                { id: 'partly_furnished', label: isRTL ? 'مؤثث جزئياً' : 'Partly' },
                { id: 'furnished', label: isRTL ? 'مؤثث' : 'Furnished' },
              ].map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, furnishingStatus: option.id as FurnishingStatus }))}
                  className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
                    form.furnishingStatus === option.id
                      ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] text-[#003930]'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Description */}
      <section>
        <h2 className="text-xl font-semibold text-[var(--pv-color-primary)] mb-4">
          <i className="ri-edit-line me-2 text-[#004D40]"></i>
          {isRTL ? 'الوصف' : 'Description'}
        </h2>
        <textarea
          id="description"
          name="description"
          value={form.description}
          onChange={handleChange}
          rows={4}
          className={`w-full rounded-lg border px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB] ${
            displayedErrors.description ? 'border-red-400' : 'border-gray-300'
          }`}
          placeholder={isRTL ? 'صف العقار بالتفصيل...' : 'Describe the property in detail...'}
        />
        {displayedErrors.description && <p className="mt-1 text-sm text-red-600">{displayedErrors.description}</p>}
      </section>

      {/* Location */}
      <section className="grid gap-6 md:grid-cols-2">
        <div className="md:col-span-2">
          <h2 className="text-xl font-semibold text-[var(--pv-color-primary)] mb-4">
            <i className="ri-map-pin-line me-2 text-[#004D40]"></i>
            {isRTL ? 'الموقع' : 'Location'}
          </h2>
        </div>
        <div className="md:col-span-2">
          <label className="text-sm font-medium text-gray-700 mb-1 block">
            {isRTL ? 'ابحث عن العنوان' : 'Search Address'} *
          </label>
          <AddressAutocomplete
            onPlaceSelect={handleAddressSelect}
            placeholder={isRTL ? 'ابدأ بكتابة العنوان...' : 'Start typing an address...'}
            error={displayedErrors.streetAddress}
            initialValue={form.streetAddress}
          />
          <p className="mt-1 text-xs text-gray-500">
            <i className="ri-information-line me-1"></i>
            {isRTL ? 'اختر من القائمة للحصول على الإحداثيات تلقائياً' : 'Select from list to auto-fill all location fields'}
          </p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700" htmlFor="city">
            {isRTL ? 'المدينة' : 'City'} *
          </label>
          <input
            id="city"
            name="city"
            value={form.city}
            onChange={handleChange}
            className={`mt-1 w-full rounded-lg border px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB] ${
              displayedErrors.city ? 'border-red-400' : 'border-gray-300'
            }`}
          />
          {displayedErrors.city && <p className="mt-1 text-sm text-red-600">{displayedErrors.city}</p>}
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700" htmlFor="region">
            {isRTL ? 'المنطقة' : 'Region'}
          </label>
          <input
            id="region"
            name="region"
            value={form.region}
            onChange={handleChange}
            className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700" htmlFor="district">
            {isRTL ? 'الحي' : 'District'}
          </label>
          <input
            id="district"
            name="district"
            value={form.district}
            onChange={handleChange}
            className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700" htmlFor="postalCode">
            {isRTL ? 'الرمز البريدي' : 'Postal Code'}
          </label>
          <input
            id="postalCode"
            name="postalCode"
            value={form.postalCode}
            onChange={handleChange}
            className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700" htmlFor="buildingNumber">
            {isRTL ? 'رقم المبنى' : 'Building Number'}
          </label>
          <input
            id="buildingNumber"
            name="buildingNumber"
            value={form.buildingNumber}
            onChange={handleChange}
            className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700" htmlFor="unitNumber">
            {isRTL ? 'رقم الوحدة' : 'Unit Number'}
          </label>
          <input
            id="unitNumber"
            name="unitNumber"
            value={form.unitNumber}
            onChange={handleChange}
            className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700" htmlFor="additionalCode">
            {isRTL ? 'الرمز الإضافي' : 'Additional Code'}
          </label>
          <input
            id="additionalCode"
            name="additionalCode"
            value={form.additionalCode}
            onChange={handleChange}
            className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700" htmlFor="latitude">
            {isRTL ? 'خط العرض' : 'Latitude'}
          </label>
          <input
            id="latitude"
            name="latitude"
            type="number"
            step="any"
            value={form.latitude}
            onChange={handleChange}
            className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
            placeholder={isRTL ? 'مثال: 21.4858' : 'e.g., 21.4858'}
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700" htmlFor="longitude">
            {isRTL ? 'خط الطول' : 'Longitude'}
          </label>
          <input
            id="longitude"
            name="longitude"
            type="number"
            step="any"
            value={form.longitude}
            onChange={handleChange}
            className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
            placeholder={isRTL ? 'مثال: 39.1925' : 'e.g., 39.1925'}
          />
        </div>
      </section>

      {/* Amenities — grouped under Bayut-style sub-headings for scannability */}
      {showAmenitiesSection && (
        <section>
          <h2 className="text-xl font-semibold text-[var(--pv-color-primary)] mb-4">
            <i className="ri-star-line me-2 text-[#004D40]"></i>
            {isRTL ? 'المميزات والمرافق' : 'Amenities & Features'}{showAmenitiesRequired ? ' *' : ''}
          </h2>
          {displayedErrors.amenities && <p id={amenitiesErrorId} className="mb-3 text-sm text-red-600">{displayedErrors.amenities}</p>}
          <div className="space-y-5">
            {AMENITY_GROUPS.map((groupDef) => {
              const items = AMENITIES_LIST.filter(
                (a) => a.group === groupDef.id && allowedAmenities.includes(a.id),
              );
              if (items.length === 0) return null;
              const groupHeadingId = `${tileIds}-amenities-${groupDef.id}`;
              return (
                <div key={groupDef.id}>
                  <h3
                    id={groupHeadingId}
                    className="mb-2.5 text-sm font-bold uppercase tracking-wide text-[var(--pv-color-text-muted)]"
                  >
                    {isRTL ? groupDef.labelAr : groupDef.label}
                  </h3>
                  {/* Checkbox tiles: each is a Tab stop and Space toggles it (native
                      button); arrows also step through the group. The check mark
                      sits at the inline end, so it flips with the reading direction. */}
                  <OptionTileGroup
                    role="group"
                    aria-labelledby={groupHeadingId}
                    aria-describedby={displayedErrors.amenities ? amenitiesErrorId : undefined}
                    className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4"
                  >
                    {items.map((amenity) => (
                      <OptionTile
                        key={amenity.id}
                        role="checkbox"
                        layout="row"
                        size="md"
                        selected={form.amenities.includes(amenity.id)}
                        onSelect={() => handleAmenityToggle(amenity.id)}
                        icon={<DomainIcon id={amenity.id} />}
                        label={isRTL ? amenity.labelAr : amenity.label}
                        className="break-words"
                      />
                    ))}
                  </OptionTileGroup>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Utilities */}
      <section>
        <h2 id={utilitiesHeadingId} className="text-xl font-semibold text-[var(--pv-color-primary)] mb-4">
          <i className="ri-service-line me-2 text-[#004D40]"></i>
          {isRTL ? 'المرافق المشمولة' : 'Utilities Included'}
        </h2>
        <p id={utilitiesHintId} className="mb-3 text-sm text-gray-500">
          {isRTL
            ? 'الإنترنت العادي والألياف خياران لاتصال واحد - اختيار أحدهما يلغي الآخر.'
            : 'Standard and fibre internet are one connection - picking one clears the other.'}
        </p>
        <OptionTileGroup
          role="group"
          aria-labelledby={utilitiesHeadingId}
          aria-describedby={utilitiesHintId}
          className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4"
        >
          {UTILITIES_LIST.map((utility) => (
            <OptionTile
              key={utility.id}
              role="checkbox"
              layout="row"
              size="md"
              selected={form.utilities.includes(utility.id)}
              onSelect={() => {
                setForm((prev) => ({
                  ...prev,
                  utilities: toggleInList(prev.utilities, utility.id, UTILITY_MUTEX_GROUPS),
                }));
              }}
              icon={<DomainIcon id={utility.id} />}
              label={isRTL ? utility.labelAr : utility.label}
              className="break-words"
            />
          ))}
        </OptionTileGroup>
      </section>

      {/* ===== Property intake v2 (Phase 4) — additive, flag-gated ===== */}
      {INTAKE_V2_ENABLED && (
        <section>
          <h2 className="text-xl font-semibold text-[var(--pv-color-primary)] mb-1">
            <i className="ri-sparkling-2-line me-2 text-[#004D40]"></i>
            {isRTL ? 'تفاصيل إضافية' : 'Additional details'}
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            {isRTL
              ? 'حقول اختيارية تساعد على تصنيف وعرض العقار بدقّة أكبر.'
              : 'Optional fields that help categorise and present the listing more precisely.'}
          </p>

          <div className="space-y-5">
            {/* Category + facade */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  {isRTL ? 'فئة العقار' : 'Property category'}
                </label>
                <select
                  name="intakeCategory" value={form.intakeCategory} onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
                >
                  <option value="">{isRTL ? 'اختر' : 'Select'}</option>
                  <option value="RESIDENTIAL">{isRTL ? 'سكني' : 'Residential'}</option>
                  <option value="COMMERCIAL">{isRTL ? 'تجاري' : 'Commercial'}</option>
                  <option value="MIXED">{isRTL ? 'مختلط (سكني/تجاري)' : 'Mixed-use'}</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  {isRTL ? 'اتجاه الواجهة' : 'Facade direction'}
                </label>
                <select
                  name="facadeDirection" value={form.facadeDirection} onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
                >
                  <option value="">{isRTL ? 'اختر' : 'Select'}</option>
                  {FACADE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{isRTL ? o.labelAr : o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Flats + shops — shown for mixed-use / commercial buildings */}
            {(form.intakeCategory === 'MIXED' || form.intakeCategory === 'COMMERCIAL') && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {isRTL ? 'عدد الشقق' : 'Number of flats'}
                  </label>
                  <input
                    type="number" min="0" name="flatsCount" dir="ltr" value={form.flatsCount} onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {isRTL ? 'عدد المحلات' : 'Number of shops'}
                  </label>
                  <input
                    type="number" min="0" name="shopsCount" dir="ltr" value={form.shopsCount} onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
                  />
                </div>
              </div>
            )}

            {/* Offering types (multi-select) */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                {isRTL ? 'أنواع العرض (يمكن اختيار أكثر من نوع)' : 'Offering types (select all that apply)'}
              </label>
              <div className="flex flex-wrap gap-2">
                {OFFERING_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, offeringTypes: toggleInList(prev.offeringTypes, o.value) }))}
                    className={`py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
                      form.offeringTypes.includes(o.value)
                        ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] text-[#003930]'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <i className={`${o.icon} me-1`}></i>
                    {isRTL ? o.labelAr : o.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Display status — public vs private (off-market) */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                {isRTL ? 'حالة العرض' : 'Display status'}
              </label>
              <div className="flex gap-2">
                {([
                  { value: 'PUBLIC' as const, label: 'Public', labelAr: 'عام', icon: 'ri-global-line' },
                  { value: 'PRIVATE' as const, label: 'Private deal', labelAr: 'صفقة خاصة', icon: 'ri-lock-2-line' },
                ]).map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, displayVisibility: o.value }))}
                    className={`flex-1 py-3 px-3 rounded-lg border text-sm font-medium transition-all ${
                      form.displayVisibility === o.value
                        ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] text-[#003930]'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <i className={`${o.icon} me-1`}></i>
                    {isRTL ? o.labelAr : o.label}
                  </button>
                ))}
              </div>
              {form.displayVisibility === 'PRIVATE' && (
                <p className="mt-2 text-xs text-gray-500 leading-relaxed">
                  {isRTL
                    ? 'لن يُنشر هذا العقار علناً؛ سيتم إبلاغ قسم التسويق للتعامل معه كصفقة خاصة. عند تسويق صفقات 24 للعقار برخصة الفال الخاصة بها، تُحتسب عمولة 0.15% من سعر البيع، أو 0.15% من إيجار السنة الأولى.'
                    : 'This listing will not be published publicly; the marketing team is notified to handle it as a private deal. When Safaqat24 markets it under its own FAL licence, the fee is 0.15% of the sale price, or 0.15% of the first year’s rent.'}
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ===== REGA Documents & License ===== */}
      <section className="rounded-xl border border-gray-200">
        <button
          type="button"
          onClick={() => setShowRegaSection((v) => !v)}
          className="flex w-full items-center justify-between gap-3 px-4 py-4"
        >
          <span className="flex items-center gap-2 text-xl font-semibold text-gray-900">
            <i className="ri-government-line text-[#004D40]"></i>
            {isRTL ? 'بيانات الترخيص والوثائق' : 'License & Documents'}
            <span className="text-xs font-normal text-gray-400">
              {isRTL ? '(الهيئة العامة للعقار)' : '(REGA)'}
            </span>
          </span>
          <i className={`text-2xl text-gray-400 ${showRegaSection ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
        </button>

        {showRegaSection && (
          <div className="space-y-6 border-t border-gray-100 px-4 py-5">
            {/* Advertising license */}
            <div>
              <h3 className="mb-3 text-sm font-bold text-gray-800">
                {isRTL ? 'ترخيص الإعلان' : 'Advertising License'}
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {isRTL ? 'رقم رخصة فال' : 'FAL License Number'}
                    {isBrokerLike && <span className="text-red-500"> *</span>}
                  </label>
                  <input
                    id="falLicenseNumber"
                    type="text"
                    name="falLicenseNumber"
                    dir="ltr"
                    value={form.falLicenseNumber}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
                  />
                  {displayedErrors.falLicenseNumber && (
                    <p className="mt-1 text-xs text-red-500">{displayedErrors.falLicenseNumber}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {isRTL ? 'رقم ترخيص الإعلان' : 'Advertisement License Number'}
                    {isBrokerLike && <span className="text-red-500"> *</span>}
                  </label>
                  <input
                    id="adLicenseNumber"
                    type="text"
                    name="adLicenseNumber"
                    dir="ltr"
                    value={form.adLicenseNumber}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
                  />
                  {displayedErrors.adLicenseNumber && (
                    <p className="mt-1 text-xs text-red-500">{displayedErrors.adLicenseNumber}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {isRTL ? 'تاريخ إصدار الترخيص' : 'License Issue Date'}
                  </label>
                  <input
                    type="date"
                    name="licenseIssueDate"
                    value={form.licenseIssueDate}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {isRTL ? 'تاريخ انتهاء الترخيص' : 'License Expiry Date'}
                  </label>
                  <input
                    type="date"
                    name="licenseExpiryDate"
                    value={form.licenseExpiryDate}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
                  />
                </div>
              </div>
            </div>

            {/* Deed & plot */}
            <div>
              <h3 className="mb-3 text-sm font-bold text-gray-800">
                {isRTL ? 'الصك والمخطط' : 'Deed & Plot'}
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {isRTL ? 'رقم صك الملكية' : 'Title Deed Number'}
                  </label>
                  <input
                    type="text" name="deedNumber" dir="ltr" value={form.deedNumber} onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {isRTL ? 'حالة الصك' : 'Deed Status'}
                  </label>
                  <select
                    name="deedStatus" value={form.deedStatus} onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
                  >
                    <option value="">{isRTL ? 'اختر' : 'Select'}</option>
                    {DEED_STATUS_OPTIONS.map((o) => (
                      <option key={o.id} value={o.id}>{isRTL ? o.labelAr : o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {isRTL ? 'رقم المخطط' : 'Plan Number'}
                  </label>
                  <input
                    type="text" name="planNumber" dir="ltr" value={form.planNumber} onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {isRTL ? 'رقم الأرض' : 'Land/Parcel Number'}
                  </label>
                  <input
                    type="text" name="landNumber" dir="ltr" value={form.landNumber} onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {isRTL ? 'نوع المالك' : 'Owner Type'}
                  </label>
                  <select
                    name="ownerType" value={form.ownerType} onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
                  >
                    <option value="">{isRTL ? 'اختر' : 'Select'}</option>
                    {OWNER_TYPE_OPTIONS.map((o) => (
                      <option key={o.id} value={o.id}>{isRTL ? o.labelAr : o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Property attributes */}
            <div>
              <h3 className="mb-3 text-sm font-bold text-gray-800">
                {isRTL ? 'خصائص العقار' : 'Property Attributes'}
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {isRTL ? 'واجهة العقار' : 'Facade / Orientation'}
                  </label>
                  <select
                    name="orientation" value={form.orientation} onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
                  >
                    <option value="">{isRTL ? 'اختر' : 'Select'}</option>
                    {ORIENTATION_OPTIONS.map((o) => (
                      <option key={o.id} value={o.id}>{isRTL ? o.labelAr : o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {isRTL ? 'حالة البناء' : 'Construction Status'}
                  </label>
                  <select
                    name="constructionStatus" value={form.constructionStatus} onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
                  >
                    <option value="">{isRTL ? 'اختر' : 'Select'}</option>
                    {CONSTRUCTION_STATUS_OPTIONS.map((o) => (
                      <option key={o.id} value={o.id}>{isRTL ? o.labelAr : o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {isRTL ? 'عمر العقار (سنوات)' : 'Property Age (years)'}
                  </label>
                  <input
                    type="number" min="0" name="propertyAge" dir="ltr" value={form.propertyAge} onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {isRTL ? 'عرض الشارع (متر)' : 'Street Width (m)'}
                  </label>
                  <input
                    type="number" min="0" name="streetWidth" dir="ltr" value={form.streetWidth} onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {isRTL ? 'الضمانات والمدة' : 'Warranty & Duration'}
                  </label>
                  <input
                    type="text" name="warranty" value={form.warranty} onChange={handleChange}
                    placeholder={isRTL ? 'مثال: ضمان سنة على الإنشاءات' : 'e.g. 1-year structural warranty'}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
                  />
                </div>
              </div>
            </div>

            {/* Construction progress — only relevant when the property isn't ready
                yet (under construction / off-plan). Hidden otherwise. All optional. */}
            {(form.constructionStatus === 'UNDER_CONSTRUCTION' || form.constructionStatus === 'OFF_PLAN') && (
              <div>
                <h3 className="mb-3 text-sm font-bold text-gray-800">
                  {isRTL ? 'تقدّم الإنشاء' : 'Construction Progress'}
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      {isRTL ? 'مرحلة الإنشاء' : 'Construction Stage'}
                    </label>
                    <select
                      name="constructionStage" value={form.constructionStage} onChange={handleChange}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
                    >
                      <option value="">{isRTL ? 'اختر' : 'Select'}</option>
                      {CONSTRUCTION_STAGE_OPTIONS.map((o) => (
                        <option key={o.id} value={o.id}>{isRTL ? o.labelAr : o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      {isRTL ? 'نسبة الإنجاز (%)' : 'Progress (%)'}
                    </label>
                    <input
                      type="number" min="0" max="100" name="constructionProgress" dir="ltr"
                      value={form.constructionProgress} onChange={handleChange}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      {isRTL ? 'تاريخ بدء الإنشاء' : 'Construction Start Date'}
                    </label>
                    <input
                      type="date" name="constructionStartDate" value={form.constructionStartDate} onChange={handleChange}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      {isRTL ? 'تاريخ الإنجاز المتوقع' : 'Expected Completion Date'}
                    </label>
                    <input
                      type="date" name="expectedCompletionDate" value={form.expectedCompletionDate} onChange={handleChange}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Residential occupancy type (residential only) */}
            {form.category === 'residential' && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  {isRTL ? 'نوع السكن' : 'Residential Type'}
                </label>
                <div className="flex flex-wrap gap-2">
                  {RESIDENTIAL_TYPE_OPTIONS.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => setForm((prev) => ({
                        ...prev,
                        residentialType: (prev.residentialType === o.id ? '' : o.id) as FormState['residentialType'],
                      }))}
                      className={`py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
                        form.residentialType === o.id
                          ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] text-[#003930]'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <i className={`${o.icon} me-1`}></i>
                      {isRTL ? o.labelAr : o.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Advertising channels (multi-select) */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                {isRTL ? 'قنوات الإعلان' : 'Advertising Channels'}
              </label>
              <div className="flex flex-wrap gap-2">
                {AD_CHANNEL_OPTIONS.map((o: OptionItem) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setForm((prev) => ({
                      ...prev,
                      adChannels: prev.adChannels.includes(o.id)
                        ? prev.adChannels.filter((c) => c !== o.id)
                        : [...prev.adChannels, o.id],
                    }))}
                    className={`py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
                      form.adChannels.includes(o.id)
                        ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] text-[#003930]'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <i className={`${o.icon} me-1`}></i>
                    {isRTL ? o.labelAr : o.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Legal status flags */}
            <div>
              <h3 className="mb-3 text-sm font-bold text-gray-800">
                {isRTL ? 'الحالة النظامية' : 'Legal Status'}
              </h3>
              <div className="grid gap-3 sm:grid-cols-2">
                {([
                  { key: 'saudiBuildingCodeCompliant', labelAr: 'مطابق لكود البناء السعودي', label: 'Saudi Building Code Compliant' },
                  { key: 'encumbrance', labelAr: 'يوجد التزام على العقار', label: 'Has Encumbrance' },
                  { key: 'mortgaged', labelAr: 'العقار مرهون', label: 'Mortgaged' },
                  { key: 'restricted', labelAr: 'العقار مقيد', label: 'Restricted' },
                ] as const).map((flag) => (
                  <label key={flag.key} className="flex items-center gap-3 rounded-lg border border-gray-200 px-4 py-3 cursor-pointer hover:border-gray-300">
                    <input
                      type="checkbox"
                      checked={form[flag.key]}
                      onChange={(e) => setForm((prev) => ({ ...prev, [flag.key]: e.target.checked }))}
                      className="h-4 w-4 rounded accent-[#004D40]"
                    />
                    <span className="text-sm text-gray-700">{isRTL ? flag.labelAr : flag.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                {isRTL ? 'ملاحظات' : 'Notes'}
              </label>
              <textarea
                name="notes" rows={3} value={form.notes} onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
              />
            </div>
          </div>
        )}
      </section>

      {/* Media */}
      <section className="grid gap-6 md:grid-cols-2">
        <div className="md:col-span-2">
          <h2 className="text-xl font-semibold text-[var(--pv-color-primary)] mb-4">
            <i className="ri-image-line me-2 text-[#004D40]"></i>
            {isRTL ? 'الصور' : 'Images'}
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            {isRTL
              ? `أضف حتى ${MAX_IMAGES} صورة. اضغط على أي صورة لتعيينها كصورة الغلاف.`
              : `Add up to ${MAX_IMAGES} images. Click any image to set it as the cover.`}
          </p>
        </div>

        {/* Upload button */}
        <div className="md:col-span-2">
          <label
            htmlFor="imageUpload"
            className="flex items-center justify-center gap-2 w-full py-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#1B7566] hover:bg-[var(--pv-color-surface-sunken)] transition-colors"
          >
            <i className="ri-upload-cloud-line text-2xl text-gray-400"></i>
            <span className="text-gray-600">
              {isRTL ? 'انقر لرفع الصور' : 'Click to upload images'}
            </span>
            {imageFiles.length > 0 && (
              <span className="text-sm text-[#004D40] font-medium">
                ({imageFiles.length}/{MAX_IMAGES})
              </span>
            )}
          </label>
          <input
            id="imageUpload"
            type="file"
            multiple
            accept="image/*"
            onChange={handleImageFiles}
            className="hidden"
            disabled={imageFiles.length >= MAX_IMAGES}
          />
        </div>

        {/* Image grid with cover selection */}
        {imagePreviews.length > 0 && (
          <div className="md:col-span-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {imagePreviews.map((preview, index) => (
              <div
                key={index}
                className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                  index === coverImageIndex
                    ? 'border-[#004D40] ring-2 ring-[#DDEEEB]'
                    : 'border-gray-200 hover:border-[var(--pv-color-accent-soft)]'
                }`}
                onClick={() => handleSetCoverImage(index)}
              >
                <img
                  src={preview}
                  alt={`Image ${index + 1}`}
                  className="w-full h-32 object-cover"
                />
                {/* Cover badge */}
                {index === coverImageIndex && (
                  <div className="absolute top-2 start-2 bg-[#004D40] text-white text-xs px-2 py-1 rounded-full font-medium">
                    {isRTL ? 'الغلاف' : 'Cover'}
                  </div>
                )}
                {/* Remove button */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage(index);
                  }}
                  className="absolute top-2 end-2 bg-red-500 text-white w-6 h-6 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                  title={isRTL ? 'إزالة' : 'Remove'}
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
                {/* Floor plan toggle */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleFloorPlan(index);
                  }}
                  className={`absolute bottom-8 start-0 end-0 text-xs py-1 text-center transition-opacity ${
                    floorPlanIndices.has(index)
                      ? 'bg-amber-500 text-white opacity-100'
                      : 'bg-black/50 text-white opacity-0 group-hover:opacity-100'
                  }`}
                  title={floorPlanIndices.has(index) ? (isRTL ? 'مخطط' : 'Floor plan') : (isRTL ? 'تعيين كمخطط' : 'Set as floor plan')}
                >
                  <i className="ri-layout-line me-1 text-xs"></i>
                  {floorPlanIndices.has(index) ? (isRTL ? 'مخطط' : 'Floor plan') : (isRTL ? 'تعيين كمخطط' : 'Set as floor plan')}
                </button>
                {/* Tap to set cover hint */}
                {index !== coverImageIndex && (
                  <div className="absolute bottom-0 start-0 end-0 bg-black/50 text-white text-xs py-1 text-center opacity-0 group-hover:opacity-100 transition-opacity">
                    {isRTL ? 'اضغط للغلاف' : 'Click for cover'}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Manual URL input (fallback) */}
        <div>
          <label className="text-sm font-medium text-gray-700" htmlFor="imageUrl">
            {isRTL ? 'أو أدخل رابط صورة' : 'Or enter image URL'}
          </label>
          <input
            id="imageUrl"
            type="url"
            name="imageUrl"
            value={form.imageUrl}
            onChange={handleChange}
            className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
            placeholder="https://example.com/image.jpg"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700" htmlFor="galleryUrls">
            {isRTL ? 'روابط صور إضافية' : 'Additional image URLs'}
          </label>
          <textarea
            id="galleryUrls"
            name="galleryUrls"
            value={form.galleryUrls}
            onChange={handleChange}
            rows={2}
            className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
            placeholder={isRTL ? 'رابط واحد لكل سطر' : 'One URL per line'}
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700" htmlFor="videoTourUrl">
            <i className="ri-video-line me-1 text-gray-500"></i>
            {isRTL ? 'رابط جولة فيديو' : 'Video Tour URL'}
          </label>
          <input
            id="videoTourUrl"
            type="url"
            name="videoTourUrl"
            value={form.videoTourUrl}
            onChange={handleChange}
            className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
            placeholder="https://youtube.com/..."
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700" htmlFor="virtualTourUrl">
            <i className="ri-360-line me-1 text-gray-500"></i>
            {isRTL ? 'رابط جولة افتراضية' : 'Virtual Tour URL'}
          </label>
          <input
            id="virtualTourUrl"
            type="url"
            name="virtualTourUrl"
            value={form.virtualTourUrl}
            onChange={handleChange}
            className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
            placeholder="https://..."
          />
        </div>
      </section>

      {/* Contact — for the individual variant these are the OWNER's private
          details (kept off the public listing); for brokers, the public agent. */}
      <section className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-3">
          <h2 className={`text-xl font-semibold text-[var(--pv-color-primary)] ${contactHelp ? 'mb-1' : 'mb-4'}`}>
            <i className={`me-2 text-[#004D40] ${isIndividual ? 'ri-shield-user-line' : 'ri-user-line'}`}></i>
            {contactHeading}
          </h2>
          {contactHelp && (
            <p className="mb-4 text-sm text-gray-500">
              <i className="ri-lock-line me-1"></i>
              {contactHelp}
            </p>
          )}
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700" htmlFor="agentName">
            {isRTL ? 'الاسم' : 'Name'} *
          </label>
          <input
            id="agentName"
            name="agentName"
            value={form.agentName}
            onChange={handleChange}
            className={`mt-1 w-full rounded-lg border px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB] ${
              displayedErrors.agentName ? 'border-red-400' : 'border-gray-300'
            }`}
          />
          {displayedErrors.agentName && <p className="mt-1 text-sm text-red-600">{displayedErrors.agentName}</p>}
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700" htmlFor="agentPhone">
            {isRTL ? 'رقم الهاتف' : 'Phone'} *
          </label>
          <input
            id="agentPhone"
            name="agentPhone"
            value={form.agentPhone}
            onChange={handleChange}
            className={`mt-1 w-full rounded-lg border px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB] ${
              displayedErrors.agentPhone ? 'border-red-400' : 'border-gray-300'
            }`}
            placeholder="+966 55 123 4567"
          />
          {displayedErrors.agentPhone && <p className="mt-1 text-sm text-red-600">{displayedErrors.agentPhone}</p>}
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700" htmlFor="agentEmail">
            {isRTL ? 'البريد الإلكتروني' : 'Email'} *
          </label>
          <input
            id="agentEmail"
            type="email"
            name="agentEmail"
            value={form.agentEmail}
            onChange={handleChange}
            className={`mt-1 w-full rounded-lg border px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB] ${
              displayedErrors.agentEmail ? 'border-red-400' : 'border-gray-300'
            }`}
          />
          {displayedErrors.agentEmail && <p className="mt-1 text-sm text-red-600">{displayedErrors.agentEmail}</p>}
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700" htmlFor="agentWhatsapp">
            <i className="ri-whatsapp-line me-1 text-green-600"></i>
            {isRTL ? 'واتساب' : 'WhatsApp'}
          </label>
          <input
            id="agentWhatsapp"
            name="agentWhatsapp"
            value={form.agentWhatsapp}
            onChange={handleChange}
            className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#DDEEEB]"
            placeholder="+966 55 123 4567"
          />
        </div>
      </section>

      {/* Submit */}
      <footer className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between pt-6 border-t">
        {onReset && (
          <Button type="button" variant="outline" onClick={handleResetClick} disabled={submitting || formDisabled}>
            <i className="ri-refresh-line me-2"></i>
            {resetLabel ?? (isRTL ? 'إعادة تعيين' : 'Reset Form')}
          </Button>
        )}
        <Button type="submit" disabled={submitting || formDisabled} className="bg-[#004D40] hover:bg-[#003930]">
          {formDisabled ? (
            <>{disabledLabel ?? (isRTL ? 'الحد الأقصى للعقارات' : 'Listing Limit Reached')}</>
          ) : submitting ? (
            <>
              <i className="ri-loader-4-line animate-spin me-2"></i>
              {uploadProgress || (isRTL ? 'جارٍ النشر...' : 'Publishing...')}
            </>
          ) : (
            <>
              <i className="ri-send-plane-line me-2"></i>
              {submitLabel}
            </>
          )}
        </Button>
      </footer>
    </form>
  );
}
